import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Search,
  MoreVertical,
  Eye,
  RefreshCw,
  XCircle,
  CreditCard,
  ArrowUpDown,
  CalendarPlus,
  Play,
  ExternalLink,
  Filter,
  Activity
} from 'lucide-react';
import { adminService } from '../../services/adminService';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import AssignPlanModal from '../../components/admin/AssignPlanModal';
import ExtendPeriodModal from '../../components/admin/ExtendPeriodModal';
import UserAuditLogModal from '../../components/admin/UserAuditLogModal';
import { Subscription, User, Plan } from '../../types';

interface ExtendedSubscription extends Subscription {
  user?: User;
  plan?: Plan;
}

const AdminSubscriptionsPage: React.FC = () => {
  const { t } = useTranslation();
  const [subscriptions, setSubscriptions] = useState<ExtendedSubscription[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [billingCycleFilter, setBillingCycleFilter] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [manualOnly, setManualOnly] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedSubscription, setSelectedSubscription] = useState<ExtendedSubscription | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState<string | null>(null);

  useEffect(() => {
    loadPlans();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadSubscriptions();
    }, search ? 400 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, planFilter, billingCycleFilter, createdFrom, createdTo, manualOnly, search]);

  const loadPlans = async () => {
    try {
      const response = await adminService.getPlans();
      if (response.success && response.data) setPlans(response.data);
    } catch (error) {
      console.error('Error loading plans:', error);
    }
  };

  const loadSubscriptions = async () => {
    setIsLoading(true);
    try {
      const response = await adminService.getSubscriptions({
        page,
        limit: 15,
        search: search || undefined,
        status: statusFilter || undefined,
        plan_id: planFilter || undefined,
        billing_cycle: billingCycleFilter || undefined,
        created_from: createdFrom || undefined,
        created_to: createdTo || undefined,
        manual_only: manualOnly ? 'true' : undefined
      });
      if (response.success && response.data) {
        setSubscriptions(response.data as ExtendedSubscription[]);
        setTotalPages(response.pagination?.totalPages || 1);
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setPlanFilter('');
    setBillingCycleFilter('');
    setCreatedFrom('');
    setCreatedTo('');
    setManualOnly(false);
    setPage(1);
  };

  const handleViewSubscription = (subscription: ExtendedSubscription) => {
    setSelectedSubscription(subscription);
    setIsDetailModalOpen(true);
    setIsActionMenuOpen(null);
  };

  const handleChangePlan = (subscription: ExtendedSubscription) => {
    setSelectedSubscription(subscription);
    setIsAssignModalOpen(true);
    setIsActionMenuOpen(null);
  };

  const handleExtend = (subscription: ExtendedSubscription) => {
    setSelectedSubscription(subscription);
    setIsExtendModalOpen(true);
    setIsActionMenuOpen(null);
  };

  const handleAuditLog = (subscription: ExtendedSubscription) => {
    setSelectedSubscription(subscription);
    setIsAuditModalOpen(true);
    setIsActionMenuOpen(null);
  };

  const handleReactivate = async (subscriptionId: string) => {
    if (!window.confirm(t('admin.confirmReactivate'))) return;
    try {
      await adminService.reactivateSubscription(subscriptionId);
      toast.success(t('admin.reactivateSuccess'));
      loadSubscriptions();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.generic'));
    }
    setIsActionMenuOpen(null);
  };

  const handleCancelSubscription = async (subscriptionId: string) => {
    if (!window.confirm(t('admin.confirmCancelSubscription'))) return;
    try {
      await adminService.cancelSubscription(subscriptionId);
      toast.success(t('messages.updatedSuccessfully'));
      loadSubscriptions();
    } catch (error) {
      toast.error(t('errors.generic'));
    }
    setIsActionMenuOpen(null);
  };

  const handleRefundSubscription = async (subscriptionId: string) => {
    if (!window.confirm(t('admin.confirmRefund'))) return;
    try {
      await adminService.refundSubscription(subscriptionId);
      toast.success(t('admin.refundProcessed'));
      loadSubscriptions();
    } catch (error) {
      toast.error(t('errors.generic'));
    }
    setIsActionMenuOpen(null);
  };

  const openInStripe = (sub: ExtendedSubscription) => {
    if (!sub.stripe_subscription_id) return;
    const url = `https://dashboard.stripe.com/subscriptions/${sub.stripe_subscription_id}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setIsActionMenuOpen(null);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'danger' | 'gray' | 'primary'> = {
      active: 'success',
      trialing: 'primary',
      past_due: 'warning',
      canceled: 'danger',
      inactive: 'gray',
      unpaid: 'danger',
      paused: 'warning'
    };
    return variants[status] || 'gray';
  };

  const formatPrice = (sub: ExtendedSubscription) => {
    if (sub.metadata?.is_free) return t('admin.freePlan');
    if (sub.metadata?.custom_price !== undefined && sub.metadata?.custom_price !== null) {
      return Number(sub.metadata.custom_price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }
    const price = sub.billing_cycle === 'monthly' ? sub.plan?.price_monthly : sub.plan?.price_yearly;
    return price?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) || '-';
  };

  const filtersAreActive = useMemo(() =>
    !!(search || statusFilter || planFilter || billingCycleFilter || createdFrom || createdTo || manualOnly),
  [search, statusFilter, planFilter, billingCycleFilter, createdFrom, createdTo, manualOnly]);

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('admin.subscriptions')}</h1>
        <p className="text-gray-600 dark:text-gray-400">{t('admin.manageSubscriptions')}</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col md:flex-row gap-3">
              <Input
                wrapperClassName="flex-1"
                placeholder={t('admin.searchSubscriptions')}
                value={search}
                onChange={(e) => { setPage(1); setSearch(e.target.value); }}
                leftIcon={<Search className="w-5 h-5" />}
              />
              <Select
                wrapperClassName="w-full md:w-44"
                options={[
                  { value: '', label: t('admin.allStatus') },
                  { value: 'active', label: t('subscription.status.active') },
                  { value: 'trialing', label: t('subscription.status.trialing') },
                  { value: 'past_due', label: t('subscription.status.past_due') },
                  { value: 'canceled', label: t('subscription.status.canceled') },
                  { value: 'paused', label: t('subscription.status.paused') }
                ]}
                value={statusFilter}
                onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
              />
              <Select
                wrapperClassName="w-full md:w-44"
                options={[
                  { value: '', label: t('admin.allPlans') },
                  ...plans.map((plan) => ({ value: plan.id, label: plan.name }))
                ]}
                value={planFilter}
                onChange={(e) => { setPage(1); setPlanFilter(e.target.value); }}
              />
              <Button variant="ghost" onClick={() => setShowAdvanced((v) => !v)} className="md:w-auto">
                <Filter className="w-4 h-4 mr-2" />
                {t('admin.filterByDate')}
              </Button>
              {filtersAreActive && (
                <Button variant="secondary" onClick={clearFilters} className="md:w-auto">
                  <XCircle className="w-4 h-4 mr-2" />
                  {t('common.clear', 'Limpar')}
                </Button>
              )}
            </div>
            {showAdvanced && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                <Select
                  wrapperClassName="w-full"
                  label={t('admin.filterByCycle')}
                  options={[
                    { value: '', label: t('admin.billingCycleAll') },
                    { value: 'monthly', label: t('admin.billingCycleMonthly') },
                    { value: 'yearly', label: t('admin.billingCycleYearly') }
                  ]}
                  value={billingCycleFilter}
                  onChange={(e) => { setPage(1); setBillingCycleFilter(e.target.value); }}
                />
                <Input
                  wrapperClassName="w-full"
                  label={t('admin.createdFrom')}
                  type="date"
                  value={createdFrom}
                  onChange={(e) => { setPage(1); setCreatedFrom(e.target.value); }}
                />
                <Input
                  wrapperClassName="w-full"
                  label={t('admin.createdTo')}
                  type="date"
                  value={createdTo}
                  onChange={(e) => { setPage(1); setCreatedTo(e.target.value); }}
                />
                <div className="flex items-end pb-1">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={manualOnly}
                      onChange={(e) => { setPage(1); setManualOnly(e.target.checked); }}
                    />
                    {t('admin.manualOnly')}
                  </label>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Subscriptions Table */}
      <Card padding={false} className="overflow-visible">
        <div className="overflow-x-auto" style={{ overflow: 'visible' }}>
          {isLoading ? (
            <div className="p-8 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 skeleton rounded-lg" />
              ))}
            </div>
          ) : subscriptions.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">{t('common.noData')}</p>
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>{t('admin.user')}</th>
                  <th>{t('admin.plan')}</th>
                  <th>{t('admin.status')}</th>
                  <th>{t('admin.billingCycle')}</th>
                  <th>{t('admin.amount')}</th>
                  <th>{t('admin.startDate')}</th>
                  <th>{t('admin.nextBilling')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map((subscription) => (
                  <tr key={subscription.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                          <span className="text-primary-600 font-medium">
                            {(subscription.user?.name || subscription.user?.email || '?').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{subscription.user?.name || '-'}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{subscription.user?.email || '-'}</p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="flex flex-col gap-1">
                        <span className="font-medium text-gray-900 dark:text-white">{subscription.plan?.name || '-'}</span>
                        {subscription.metadata?.is_manual && (
                          <Badge variant="warning">{t('admin.manualSubscription')}</Badge>
                        )}
                        {subscription.metadata?.is_free && !subscription.metadata?.is_manual && (
                          <Badge variant="primary">{t('admin.freePlan')}</Badge>
                        )}
                      </div>
                    </td>
                    <td>
                      <Badge variant={getStatusBadge(subscription.status)}>
                        {t(`subscription.status.${subscription.status}`)}
                      </Badge>
                    </td>
                    <td>{t(`subscription.billingCycle.${subscription.billing_cycle}`)}</td>
                    <td className="font-medium">{formatPrice(subscription)}</td>
                    <td className="text-gray-500 dark:text-gray-400">
                      {subscription.current_period_start
                        ? new Date(subscription.current_period_start).toLocaleDateString('pt-BR')
                        : '-'}
                    </td>
                    <td className="text-gray-500 dark:text-gray-400">
                      {subscription.current_period_end
                        ? new Date(subscription.current_period_end).toLocaleDateString('pt-BR')
                        : '-'}
                    </td>
                    <td>
                      <button
                        data-subscription-id={subscription.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsActionMenuOpen(isActionMenuOpen === subscription.id ? null : subscription.id);
                        }}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                      >
                        <MoreVertical className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-t border-gray-100 dark:border-gray-700">
            <Button variant="ghost" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
              {t('common.previous')}
            </Button>
            <span className="text-sm text-gray-600 dark:text-gray-400">{page} / {totalPages}</span>
            <Button variant="ghost" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
              {t('common.next')}
            </Button>
          </div>
        )}
      </Card>

      {/* Action Menu */}
      {isActionMenuOpen && (() => {
        const sub = subscriptions.find(s => s.id === isActionMenuOpen);
        if (!sub) return null;
        const isCanceled = sub.status === 'canceled';
        const isActiveLike = sub.status === 'active' || sub.status === 'trialing';
        return (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsActionMenuOpen(null)} />
            <div
              className="fixed z-50 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1"
              style={{
                top: document.querySelector(`[data-subscription-id="${isActionMenuOpen}"]`)?.getBoundingClientRect().bottom ?? 0,
                left: Math.min(
                  (document.querySelector(`[data-subscription-id="${isActionMenuOpen}"]`)?.getBoundingClientRect().right ?? 0) - 224,
                  window.innerWidth - 232
                )
              }}
            >
              <button onClick={() => handleViewSubscription(sub)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                <Eye className="w-4 h-4" />
                {t('common.view')}
              </button>
              <button onClick={() => handleChangePlan(sub)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                <ArrowUpDown className="w-4 h-4" />
                {t('admin.changePlan')}
              </button>
              <button onClick={() => handleExtend(sub)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                <CalendarPlus className="w-4 h-4" />
                {t('admin.extendPeriod')}
              </button>
              {isCanceled && (
                <button onClick={() => handleReactivate(sub.id)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20">
                  <Play className="w-4 h-4" />
                  {t('admin.reactivate')}
                </button>
              )}
              {sub.stripe_subscription_id && (
                <button onClick={() => openInStripe(sub)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                  <ExternalLink className="w-4 h-4" />
                  {t('admin.openInStripe')}
                </button>
              )}
              <button onClick={() => handleAuditLog(sub)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                <Activity className="w-4 h-4" />
                {t('admin.viewAuditLog')}
              </button>
              {isActiveLike && (
                <>
                  <hr className="my-1 border-gray-200 dark:border-gray-700" />
                  <button onClick={() => handleRefundSubscription(sub.id)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <RefreshCw className="w-4 h-4" />
                    {t('admin.refund')}
                  </button>
                  <button onClick={() => handleCancelSubscription(sub.id)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 dark:text-red-400">
                    <XCircle className="w-4 h-4" />
                    {t('admin.cancelSubscription')}
                  </button>
                </>
              )}
            </div>
          </>
        );
      })()}

      {/* Subscription Detail Modal */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        title={t('admin.subscriptionDetails')}
        size="lg"
      >
        {selectedSubscription && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                <span className="text-xl text-primary-600 font-bold">
                  {(selectedSubscription.user?.name || selectedSubscription.user?.email || '?').charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">{selectedSubscription.user?.name}</h3>
                <p className="text-gray-500 dark:text-gray-400">{selectedSubscription.user?.email}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.plan')}</p>
                <p className="font-medium text-gray-900 dark:text-white">{selectedSubscription.plan?.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.status')}</p>
                <Badge variant={getStatusBadge(selectedSubscription.status)}>
                  {t(`subscription.status.${selectedSubscription.status}`)}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.billingCycle')}</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {t(`subscription.billingCycle.${selectedSubscription.billing_cycle}`)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.amount')}</p>
                <p className="font-medium text-gray-900 dark:text-white">{formatPrice(selectedSubscription)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.startDate')}</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {selectedSubscription.current_period_start
                    ? new Date(selectedSubscription.current_period_start).toLocaleDateString('pt-BR')
                    : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.nextBilling')}</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {selectedSubscription.current_period_end
                    ? new Date(selectedSubscription.current_period_end).toLocaleDateString('pt-BR')
                    : '-'}
                </p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.stripeSubscriptionId')}</p>
                <div className="flex items-center gap-2">
                  <p className="font-medium text-xs font-mono text-gray-900 dark:text-white truncate">
                    {selectedSubscription.stripe_subscription_id || '-'}
                  </p>
                  {selectedSubscription.stripe_subscription_id && (
                    <button
                      onClick={() => openInStripe(selectedSubscription)}
                      className="p-1 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded"
                      title={t('admin.openInStripe') || ''}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              {selectedSubscription.metadata?.admin_notes && (
                <div className="sm:col-span-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.notes')}</p>
                  <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{selectedSubscription.metadata.admin_notes}</p>
                </div>
              )}
            </div>

            {selectedSubscription.plan?.limits && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">{t('admin.planFeatures')}</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('admin.campaigns')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{selectedSubscription.plan.limits.campaigns_limit ?? selectedSubscription.plan.limits.campaigns}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('admin.keywords')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{selectedSubscription.plan.limits.keywords_limit ?? selectedSubscription.plan.limits.keywords}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('admin.competitors')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{selectedSubscription.plan.limits.competitors_limit ?? selectedSubscription.plan.limits.competitors}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('admin.contentGenerations')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{selectedSubscription.plan.limits.content_generations_limit ?? selectedSubscription.plan.limits.content_generations}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-wrap justify-end gap-3">
              <Button variant="secondary" onClick={() => setIsDetailModalOpen(false)}>
                {t('common.close')}
              </Button>
              <Button variant="ghost" onClick={() => { setIsDetailModalOpen(false); handleChangePlan(selectedSubscription); }}>
                <ArrowUpDown className="w-4 h-4 mr-2" />
                {t('admin.changePlan')}
              </Button>
              <Button variant="ghost" onClick={() => { setIsDetailModalOpen(false); handleExtend(selectedSubscription); }}>
                <CalendarPlus className="w-4 h-4 mr-2" />
                {t('admin.extendPeriod')}
              </Button>
              {selectedSubscription.status === 'canceled' ? (
                <Button onClick={() => { setIsDetailModalOpen(false); handleReactivate(selectedSubscription.id); }}>
                  <Play className="w-4 h-4 mr-2" />
                  {t('admin.reactivate')}
                </Button>
              ) : (selectedSubscription.status === 'active' || selectedSubscription.status === 'trialing') && (
                <Button
                  variant="primary"
                  className="bg-red-600 hover:bg-red-700"
                  onClick={() => { setIsDetailModalOpen(false); handleCancelSubscription(selectedSubscription.id); }}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  {t('admin.cancelSubscription')}
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>

      <AssignPlanModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        onSuccess={loadSubscriptions}
        subscription={selectedSubscription || undefined}
        plans={plans}
      />

      <ExtendPeriodModal
        isOpen={isExtendModalOpen}
        onClose={() => setIsExtendModalOpen(false)}
        onSuccess={loadSubscriptions}
        subscription={selectedSubscription}
      />

      <UserAuditLogModal
        isOpen={isAuditModalOpen}
        onClose={() => setIsAuditModalOpen(false)}
        userId={selectedSubscription?.user_id || null}
        userLabel={selectedSubscription?.user?.email}
      />
    </div>
  );
};

export default AdminSubscriptionsPage;
